import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input, Label, Textarea } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Badge } from "./ui/badge";
import { Leaf, Users, ShieldCheck, Plus, Check, X, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const PILLAR_META = {
  Environment: { icon: Leaf, color: "text-[#00E65B]" },
  Social: { icon: Users, color: "text-blue-400" },
  Governance: { icon: ShieldCheck, color: "text-[#FFB800]" },
};

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function AddEsgDataDialog({ open, onClose, onSaved }) {
  const { tenant } = useAuth();
  const [taxonomy, setTaxonomy] = useState({});
  const [factors, setFactors] = useState([]);
  const [pillar, setPillar] = useState("Environment");
  const [categoryId, setCategoryId] = useState("");
  const [activity, setActivity] = useState("");
  const [f, setF] = useState({});
  const [suggestion, setSuggestion] = useState(null);
  const [adding, setAdding] = useState(null); // 'category' | 'activity'
  const [newVal, setNewVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [repLocale, setRepLocale] = useState(null);
  const [countries, setCountries] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [moneyPrev, setMoneyPrev] = useState(null);
  const autoFactorRef = React.useRef("");

  const loadTax = () => api.get("/esg-taxonomy").then((r) => setTaxonomy(r.data.taxonomy)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    if (!open) return;
    loadTax();
    api.get("/emissions/factors").then((r) => setFactors(r.data.factors)).catch(() => {});
    api.get("/locale/reporting").then((r) => setRepLocale(r.data)).catch(() => {});
    api.get("/locale/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
    api.get("/facilities").then((r) => setFacilities(r.data.facilities || [])).catch(() => {});
    setPillar("Environment"); setCategoryId(""); setActivity(""); setAdding(null); setNewVal(""); setSuggestion(null); setMoneyPrev(null);
    setF({ period: thisMonth(), value: "", unit: "", currency: "none", activity_date: "", facility: "", scope: "2", factor_code: "", coverage_pct: "", notes: "", value_type: "number", bool_value: "true", origin: "measured" });
  }, [open]);

  const facilityCountry = useMemo(() => {
    const fac = facilities.find((x) => (x.name || "").toLowerCase() === (f.facility || "").trim().toLowerCase());
    return fac?.country || null;
  }, [facilities, f.facility]);
  const localLoc = useMemo(() => {
    if (facilityCountry) {
      const c = countries.find((x) => x.country === facilityCountry);
      if (c) return { country: facilityCountry, units: c.units, currency: c.currency };
    }
    return repLocale ? { country: repLocale.country, units: repLocale.units, currency: repLocale.currency } : null;
  }, [facilityCountry, countries, repLocale]);
  const currencyOptions = useMemo(() => {
    const s = new Set();
    countries.forEach((c) => c.currency && s.add(c.currency));
    if (repLocale?.currency) s.add(repLocale.currency);
    ["USD", "SGD", "MYR", "IDR", "VND", "PHP", "EUR", "GBP"].forEach((x) => s.add(x));
    return [...s].sort();
  }, [countries, repLocale]);

  // Live FX preview — rate is only locked at save.
  useEffect(() => {
    if (!open || !f.currency || f.currency === "none" || f.value === "" || isNaN(Number(f.value))) { setMoneyPrev(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/locale/convert-money", { amount: Number(f.value), currency: f.currency, date: f.activity_date || f.period });
        setMoneyPrev(data);
      } catch { setMoneyPrev(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [open, f.currency, f.value, f.activity_date, f.period]);

  const cats = useMemo(() => taxonomy[pillar] || [], [taxonomy, pillar]);
  const selectedCat = useMemo(() => cats.find((c) => c.id === categoryId), [cats, categoryId]);
  const isGHG = pillar === "Environment" && (selectedCat?.category || "").toLowerCase().includes("ghg");
  const isEnv = pillar === "Environment";
  const isGov = pillar === "Governance";

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const pickPillar = (p) => { setPillar(p); setCategoryId(""); setActivity(""); setAdding(null); setSuggestion(null); };
  const pickCategory = (id) => { setCategoryId(id); setActivity(""); setAdding(null); setSuggestion(null); };
  const pickActivity = (a) => { setActivity(a); autoFactorRef.current = ""; set("factor_code", ""); };

  // Auto-Apply: resolve the location-aware SEFR/IPCC factor and suggest it as an
  // overridable default (never recalculates already-saved records).
  useEffect(() => {
    if (!open || !isEnv || !activity || !selectedCat) { setSuggestion(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/emissions/factors/resolve", {
          category: selectedCat.category, activity,
          entry_unit: f.unit || undefined,
          site_country: facilityCountry || undefined,
          tenant_country: tenant?.country || undefined,
        });
        setSuggestion(data);
        if (data.resolved) {
          setF((p) => {
            // Auto-apply the suggested factor when none is set, or when the user
            // hasn't overridden the last auto-applied one (so changing facility/country updates it).
            if (!p.factor_code || p.factor_code === autoFactorRef.current) {
              autoFactorRef.current = data.factor.code;
              return { ...p, factor_code: data.factor.code };
            }
            return p;
          });
        }
      } catch { setSuggestion(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [open, isEnv, activity, selectedCat, f.unit, facilityCountry, tenant]); // eslint-disable-line

  const addCategory = async () => {
    if (!newVal.trim()) return;
    try {
      const r = await api.post("/esg-taxonomy/category", { pillar, category: newVal.trim() });
      await loadTax();
      setCategoryId(r.data.id); setActivity(""); setAdding(null); setNewVal("");
      toast.success("Category added to master table");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const addActivity = async () => {
    if (!newVal.trim() || !categoryId) return;
    try {
      await api.post("/esg-taxonomy/activity", { category_id: categoryId, activity_type: newVal.trim() });
      await loadTax();
      setActivity(newVal.trim()); setAdding(null); setNewVal("");
      toast.success("Activity type added to master table");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    if (!selectedCat || !activity) { toast.error("Pick a category and activity type"); return; }
    const body = {
      pillar, category: selectedCat.category, activity_type: activity, period: f.period,
      unit: f.unit || undefined, notes: f.notes || undefined, origin: f.origin || undefined,
      currency: (f.currency && f.currency !== "none") ? f.currency : undefined,
      activity_date: f.activity_date || undefined,
    };
    if (isEnv) {
      body.value = f.value === "" ? null : Number(f.value);
      body.facility = f.facility || undefined;
      if (isGHG) { body.scope = Number(f.scope); body.factor_code = f.factor_code || undefined; }
      else if (f.factor_code) body.factor_code = f.factor_code;
    } else if (isGov && f.value_type === "boolean") {
      body.value_type = "boolean"; body.bool_value = f.bool_value === "true";
    } else {
      body.value = f.value === "" ? null : Number(f.value);
      if (pillar === "Social" && f.coverage_pct !== "") body.coverage_pct = Number(f.coverage_pct);
    }
    setSaving(true);
    try {
      await api.post("/esg-records", body);
      toast.success("ESG data captured");
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const factorPreview = useMemo(() => {
    if (!f.factor_code || !f.value) return null;
    if (suggestion?.resolved && suggestion.factor.code === f.factor_code && suggestion.resolution?.conversion?.factor_per_entry_unit != null) {
      return (Number(f.value) * suggestion.resolution.conversion.factor_per_entry_unit / 1000).toFixed(4);
    }
    const fac = factors.find((x) => x.code === f.factor_code);
    if (!fac || fac.factor_kgco2e == null) return null;
    return (Number(f.value) * fac.factor_kgco2e / 1000).toFixed(4);
  }, [f.factor_code, f.value, factors, suggestion]);

  const InlineAdd = ({ onConfirm, placeholder }) => (
    <div className="flex items-center gap-1.5 mt-1.5">
      <Input autoFocus value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder={placeholder}
        onKeyDown={(e) => e.key === "Enter" && onConfirm()} className="h-8" data-testid="taxonomy-inline-input" />
      <Button size="icon" variant="primary" className="h-8 w-8" onClick={onConfirm} data-testid="taxonomy-inline-confirm"><Check className="h-3.5 w-3.5" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setAdding(null); setNewVal(""); }}><X className="h-3.5 w-3.5" /></Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="add-esg-data-dialog">
        <DialogHeader><DialogTitle className="font-display">Add ESG data point</DialogTitle></DialogHeader>

        {/* Pillar */}
        <div>
          <Label className="text-xs">Pillar</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {Object.keys(PILLAR_META).map((p) => {
              const Icon = PILLAR_META[p].icon; const on = pillar === p;
              return (
                <button key={p} onClick={() => pickPillar(p)} data-testid={`pillar-${p.toLowerCase()}`}
                  className={`flex items-center gap-2 p-2.5 rounded-sm border text-sm transition-colors ${on ? "border-accent bg-accent/10" : "border-border hover:bg-surface-hover"}`}>
                  <Icon className={`h-4 w-4 ${PILLAR_META[p].color}`} /> {p}
                </button>
              );
            })}
          </div>
        </div>

        {/* Category (layered) */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Category</Label>
            <button className="text-[11px] text-accent inline-flex items-center gap-0.5" onClick={() => { setAdding(adding === "category" ? null : "category"); setNewVal(""); }} data-testid="add-category-toggle"><Plus className="h-3 w-3" /> Add new</button>
          </div>
          <Select value={categoryId} onValueChange={pickCategory}>
            <SelectTrigger data-testid="category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.category}</SelectItem>)}</SelectContent>
          </Select>
          {adding === "category" && <InlineAdd onConfirm={addCategory} placeholder="New category name" />}
        </div>

        {/* Activity Type (layered) */}
        {categoryId && (
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Activity type</Label>
              <button className="text-[11px] text-accent inline-flex items-center gap-0.5" onClick={() => { setAdding(adding === "activity" ? null : "activity"); setNewVal(""); }} data-testid="add-activity-toggle"><Plus className="h-3 w-3" /> Add new</button>
            </div>
            <Select value={activity} onValueChange={pickActivity}>
              <SelectTrigger data-testid="activity-select"><SelectValue placeholder="Select activity type" /></SelectTrigger>
              <SelectContent>{(selectedCat?.activity_types || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            {adding === "activity" && <InlineAdd onConfirm={addActivity} placeholder="New activity type" />}
          </div>
        )}

        {/* Dynamic attributes */}
        {activity && (
          <div className="space-y-3 border-t border-border pt-3" data-testid="dynamic-fields">
            <div className="flex items-center gap-2"><Badge variant="outline" className="text-[10px]">{pillar} · {selectedCat?.category}</Badge><span className="text-xs text-muted-foreground">{activity}</span></div>
            {localLoc && (
              <div className="text-[10px] text-muted-foreground" data-testid="facility-local-defaults">
                Local defaults{facilityCountry ? ` · ${facilityCountry}` : ""}: currency <span className="font-mono text-foreground">{localLoc.currency}</span> · area {localLoc.units?.area} · energy {localLoc.units?.energy} · volume {localLoc.units?.volume} · mass {localLoc.units?.mass}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Period (YYYY-MM)</Label><Input value={f.period} onChange={(e) => set("period", e.target.value)} placeholder="2024-06" data-testid="field-period" /></div>

              {isEnv && (
                <div><Label className="text-xs">Facility</Label>
                  <Select value={f.facility || "none"} onValueChange={(v) => set("facility", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="field-facility"><SelectValue placeholder="Select facility" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="none">— Unassigned —</SelectItem>
                      {facilities.map((x) => <SelectItem key={x.id} value={x.name}>{x.name}{x.country ? ` · ${x.country}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isGHG && (
                <div><Label className="text-xs">Scope</Label>
                  <Select value={f.scope} onValueChange={(v) => set("scope", v)}>
                    <SelectTrigger data-testid="field-scope"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="1">Scope 1</SelectItem><SelectItem value="2">Scope 2</SelectItem><SelectItem value="3">Scope 3</SelectItem></SelectContent>
                  </Select>
                </div>
              )}

              {/* Governance boolean toggle */}
              {isGov && (
                <div><Label className="text-xs">Data type</Label>
                  <Select value={f.value_type} onValueChange={(v) => set("value_type", v)}>
                    <SelectTrigger data-testid="field-valuetype"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="number">Numeric</SelectItem><SelectItem value="boolean">Yes / No</SelectItem></SelectContent>
                  </Select>
                </div>
              )}

              {isGov && f.value_type === "boolean" ? (
                <div><Label className="text-xs">Value</Label>
                  <Select value={f.bool_value} onValueChange={(v) => set("bool_value", v)}>
                    <SelectTrigger data-testid="field-boolvalue"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div><Label className="text-xs">Value</Label><Input type="number" step="any" value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="field-value" /></div>
                  <div><Label className="text-xs">Unit</Label><Input value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder={isGHG ? "kWh, litre…" : "%, hrs, cases…"} data-testid="field-unit" /></div>
                  <div><Label className="text-xs">Currency (monetary values)</Label>
                    <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
                      <SelectTrigger data-testid="field-currency"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="none">— None (physical / non-monetary) —</SelectItem>
                        {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c}{localLoc?.currency === c ? " · local" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Activity date (optional)</Label><Input type="date" value={f.activity_date} onChange={(e) => set("activity_date", e.target.value)} data-testid="field-activity-date" /></div>
                </>
              )}

              {pillar === "Social" && f.value_type !== "boolean" && (
                <div><Label className="text-xs">Coverage % (optional)</Label><Input type="number" step="any" value={f.coverage_pct} onChange={(e) => set("coverage_pct", e.target.value)} data-testid="field-coverage" /></div>
              )}

              {isEnv && (
                <div className="col-span-2"><Label className="text-xs">Emission factor {isGHG ? "" : "(optional)"}</Label>
                  {suggestion?.resolved && (
                    <div className="card-flat p-3 my-1.5 bg-accent/5 border-accent/30 space-y-1" data-testid="factor-suggestion">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={(suggestion.resolution.registry === "SEFR" || suggestion.resolution.country_specific) ? "lime" : "outline"} className="text-[10px]">{suggestion.resolution.label}</Badge>
                        <span className="text-xs font-medium">{suggestion.factor.name}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">{suggestion.factor.code}</span>
                      </div>
                      {suggestion.resolution.conversion?.applied && (
                        <div className="text-[10px] text-muted-foreground">≈ {suggestion.resolution.conversion.factor_per_entry_unit} kgCO₂e/{suggestion.resolution.conversion.entry_unit} (converted from /{suggestion.resolution.conversion.factor_unit})</div>
                      )}
                      {suggestion.resolution.fallback_reason && (
                        <div className="text-[10px] text-accent flex items-center gap-1"><Info className="h-3 w-3 shrink-0" /> Fell back to IPCC: {suggestion.resolution.fallback_reason}</div>
                      )}
                      {f.factor_code === suggestion.factor.code
                        ? <div className="text-[10px] text-muted-foreground">Applied automatically — override below if needed.</div>
                        : <button className="text-[11px] text-accent" onClick={() => set("factor_code", suggestion.factor.code)} data-testid="use-suggested-factor">Use suggested factor</button>}
                    </div>
                  )}
                  <Select value={f.factor_code || "none"} onValueChange={(v) => set("factor_code", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="field-factor"><SelectValue placeholder="Select factor" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="none">No factor</SelectItem>
                      {factors.map((x) => <SelectItem key={x.code} value={x.code}>{x.code} — {x.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {factorPreview && <div className="text-[11px] text-accent mt-1 font-mono" data-testid="factor-preview">= {factorPreview} tCO₂e</div>}
                </div>
              )}

              {f.currency && f.currency !== "none" && f.value !== "" && f.value_type !== "boolean" && (
                <div className="col-span-2 card-flat p-2.5 bg-accent/5 border-accent/30 text-[11px]" data-testid="money-preview">
                  {moneyPrev?.fx_status === "ready" && moneyPrev.amount_reporting != null ? (
                    <span>≈ <span className="font-mono text-accent">{Number(moneyPrev.amount_reporting).toLocaleString()} {moneyPrev.currency_reporting}</span> · rate {moneyPrev.fx_rate} on {moneyPrev.fx_rate_date} — <span className="text-muted-foreground">locked at save</span></span>
                  ) : (
                    <span className="text-[#FFB800]">FX rate pending for {f.currency} → {moneyPrev?.currency_reporting || repLocale?.currency} — the record saves now; the reporting rate locks automatically once available.</span>
                  )}
                </div>
              )}

              <div className="col-span-2">
                <Label className="text-xs">Evidence origin</Label>
                <Select value={f.origin} onValueChange={(v) => set("origin", v)}>
                  <SelectTrigger data-testid="field-origin"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="measured">Measured (metered / documented)</SelectItem>
                    <SelectItem value="calculated">Calculated (value × factor)</SelectItem>
                    <SelectItem value="estimate">Estimate</SelectItem>
                    <SelectItem value="declaration">Declaration (self-reported)</SelectItem>
                    <SelectItem value="ai_inferred">AI-inferred</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-0.5">Qualifies the value for Score Intelligence — measured &amp; calculated carry more confidence.</p>
              </div>

              <div className="col-span-2"><Label className="text-xs">Notes (optional)</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="field-notes" /></div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving || !activity} data-testid="save-esg-data-btn">{saving ? "Saving…" : "Save data point"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
